import React, { useState } from 'react';
import { api } from '../lib/auth';
import { Button, ModalShell } from '../lib/ui';
import { parseDraftsFile } from '../lib/outreachDrafts';

const ipcRenderer = window.process?.type === 'renderer' ? window.require('electron').ipcRenderer : null;

// Loads a file of pre-written outreach emails onto their leads as drafts.
// Nothing is sent from here: each draft then waits on its lead's page to be
// read, edited and sent by hand. In the desktop app the file is read straight
// from .outreach/drafts.json (where Claude leaves it); a file picker covers
// the browser and any other file.
export default function OutreachDraftsImport({ leads, onClose, onImported }) {
  const [rows, setRows]         = useState(null);  // null = no file parsed yet
  const [fileName, setFileName] = useState('');
  const [error, setError]       = useState('');
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult]     = useState(null);

  const selected = rows ? rows.filter(r => r.include) : [];

  const reset = (name) => {
    setRows(null); setError(''); setResult(null); setProgress(0);
    setFileName(name);
  };

  const showParsed = (json) => {
    const parsed = parseDraftsFile(json, leads);
    if (parsed.error) return setError(parsed.error);
    setRows(parsed.rows);
  };

  const loadFromDisk = async () => {
    reset('.outreach/drafts.json');
    const res = await ipcRenderer.invoke('outreach-drafts-read');
    if (res.error === 'not_found') return setError(`No drafts file yet. Expected it at ${res.path}`);
    if (res.error) return setError(`Couldn't read ${res.path} (${res.error})`);
    showParsed(res.data);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    reset(file.name);
    try {
      showParsed(JSON.parse(await file.text()));
    } catch (err) {
      setError(`Couldn't read that file (${err.message})`);
    }
  };

  const toggle = (idx) => {
    setRows(prev => prev.map((r, i) => (i === idx && !r.blocked) ? { ...r, include: !r.include } : r));
  };

  const runImport = async () => {
    if (!selected.length) return;
    setImporting(true);
    let saved = 0;
    const failed = [];
    for (let i = 0; i < selected.length; i++) {
      const r = selected[i];
      try {
        if (r.setEmail) await api.patch(`/leads/${r.leadId}`, { email: r.setEmail });
        await api.put(`/leads/${r.leadId}/draft`, { subject: r.subject, html: r.html, notes: r.notes });
        saved++;
      } catch (e) {
        failed.push(`${r.businessName}: ${e.response?.data?.error || e.message}`);
      }
      setProgress(i + 1);
    }
    setImporting(false);
    setResult({ saved, failed });
    onImported();
  };

  const cell = { padding: '6px 10px', verticalAlign: 'top' };

  return (
    <ModalShell onClose={importing ? () => {} : onClose} width={820} ariaLabel="Import outreach drafts">
      <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Import drafts</h2>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--muted)' }}>
        Each draft is saved on its lead, ready to check and send from that lead's page. Nothing is sent from here.
      </p>

      {!result && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
          {ipcRenderer && (
            <Button variant="primary" onClick={loadFromDisk} disabled={importing}>Load .outreach/drafts.json</Button>
          )}
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>{ipcRenderer ? 'or pick a file:' : 'Pick a drafts .json file:'}</span>
          <input type="file" aria-label="Drafts file" accept=".json,application/json" onChange={handleFile} disabled={importing} />
        </div>
      )}

      {error && (
        <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--r-lg)', color: '#991b1b', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {rows && !result && (
        <>
          <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 10px' }}>
            <strong>{fileName}</strong>: {rows.length} draft{rows.length !== 1 ? 's' : ''}, {selected.length} selected.
            {rows.some(r => !r.include) && ' Unticked rows need a look first; anything greyed out can\'t be imported.'}
          </p>
          <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', marginBottom: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ position: 'sticky', top: 0, background: 'var(--panel-2)' }}>
                  <th style={{ ...cell, width: 28 }}></th>
                  <th style={{ ...cell, textAlign: 'left' }}>Lead</th>
                  <th style={{ ...cell, textAlign: 'left' }}>Subject</th>
                  <th style={{ ...cell, textAlign: 'left' }}>Check</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border)', opacity: r.blocked ? 0.45 : (r.include ? 1 : 0.7) }}>
                    <td style={cell}>
                      <input type="checkbox" checked={r.include} disabled={r.blocked} onChange={() => toggle(i)} />
                    </td>
                    <td style={cell}>
                      {r.businessName || '(unnamed)'}
                      {r.to && <div style={{ color: 'var(--muted)', fontSize: 11 }}>{r.to}</div>}
                    </td>
                    <td style={cell}>{r.subject || '—'}</td>
                    <td style={{ ...cell, color: r.issues.length ? '#8c6d1f' : 'var(--muted)' }}>
                      {r.issues.length ? r.issues.join(' · ') : 'OK'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {importing && (
        <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 16px' }}>Saving… {progress} / {selected.length}</p>
      )}

      {result && (
        <div style={{ padding: '12px 16px', background: result.failed.length ? '#fff7e6' : '#f0fdf4', border: `1px solid ${result.failed.length ? '#ffd591' : '#86efac'}`, borderRadius: 'var(--r-lg)', fontSize: 14, color: result.failed.length ? '#8c6d1f' : '#166534', marginBottom: 16 }}>
          Saved {result.saved} draft{result.saved !== 1 ? 's' : ''}. Filter by "Drafts ready" to work through them.
          {result.failed.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13 }}>
              {result.failed.map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Button variant="muted" onClick={onClose} disabled={importing}>{result ? 'Close' : 'Cancel'}</Button>
        {rows && !result && (
          <Button variant="primary" onClick={runImport} disabled={importing || !selected.length}>
            {importing ? 'Saving…' : `Save ${selected.length} draft${selected.length !== 1 ? 's' : ''}`}
          </Button>
        )}
      </div>
    </ModalShell>
  );
}
